import React, { useCallback, useState } from 'react';
import {
    Artist,
    ArtistSimple,
    createArtistFromSpotify,
    postArtist,
    refreshArtistFromSpotify,
    uploadArtistImage,
} from '@round/api';
import { mapApiErrorsToFormikErrors } from '../../../../utility/utility';
import { showNotification } from '../../../../helpers';
import ArtistForm, { ArtistFormProps } from '../../components/ArtistForm/ArtistForm';
import Modal, { ModalContent, ModalTitle } from '../../../../ui/Layout/Modals/Modal/Modal';
import Button from '../../../../ui/Buttons/Button/Button';
import styles from './CreateArtistModal.module.css';
import ArtistFromSpotifyForm, {
    ArtistFromSpotifyFormProps,
} from '../../components/ArtistFromSpotifyForm/ArtistFromSpotifyForm';

type CreateArtistModalProps = {
    isOpen: boolean;
    onClose: () => void;
    onCreated: (artist: Artist | ArtistSimple) => void;
};

const CreateArtistModal = ({ isOpen, onClose, onCreated }: CreateArtistModalProps) => {
    const [isFormManual, setIsFormManual] = useState(false);

    const handleFormSwitchClick = () => {
        setIsFormManual(!isFormManual);
    };

    const handleManualFormSubmit: ArtistFormProps['handleSubmit'] = useCallback(
        async (values, helpers) => {
            try {
                helpers.setSubmitting(true);
                let response = await postArtist({
                    name: values.name,
                    spotify_artist: values.spotify_artist?.value ?? null,
                    related_artists: values.related_artists?.map((a) => a.value) ?? [],
                });

                if (response.status === 400) {
                    helpers.setErrors(mapApiErrorsToFormikErrors(response.data));
                    return;
                }

                if (values.new_image) {
                    response = await uploadArtistImage(response.data.id, values.new_image);
                }

                onCreated(response.data);
                showNotification('Artist created', 'info');
            } catch {
                showNotification("Sorry, we're experiencing technical issues", 'error');
            } finally {
                helpers.setSubmitting(false);
            }
        },
        [onCreated]
    );

    const handleSpotifyFormSubmit: ArtistFromSpotifyFormProps['handleSubmit'] = useCallback(
        async (values, { setSubmitting, setErrors }) => {
            try {
                setSubmitting(true);

                if (!values.artist) {
                    const response = await createArtistFromSpotify(values.url);
                    if (response.status === 400) {
                        setErrors({ url: response.data.error });
                        return;
                    }

                    onCreated(response.data);
                    return;
                }

                const response = await refreshArtistFromSpotify(values.artist.value, values.url);
                if (response.status === 404) {
                    showNotification('Artist not found', 'error');
                    return;
                }

                if (response.status === 400) {
                    showNotification(response.data.error, 'error');
                    return;
                }

                onCreated(response.data);
            } catch {
                setErrors({ url: 'Sorry, some error occurred' });
            } finally {
                setSubmitting(false);
                setIsFormManual(false);
            }
        },
        [onCreated]
    );

    return (
        <Modal closeOnOverlayClick isOpen={isOpen} onClose={onClose}>
            <ModalTitle>Create Artist</ModalTitle>
            <ModalContent>
                {isFormManual && (
                    <ArtistForm
                        handleSubmit={handleManualFormSubmit}
                        renderActions={({ isSubmitting, submitForm }) => (
                            <div className={styles.actions}>
                                <Button type="filled" color="black" loading={isSubmitting} onClick={submitForm}>
                                    Create
                                </Button>

                                <Button className={styles.manualButton} onClick={handleFormSwitchClick}>
                                    Or create Artist from Spotify URL
                                </Button>
                            </div>
                        )}
                    />
                )}

                {!isFormManual && (
                    <ArtistFromSpotifyForm
                        handleSubmit={handleSpotifyFormSubmit}
                        renderActions={({ isSubmitting, isValid, submitForm }) => (
                            <div className={styles.actions}>
                                <Button
                                    type="filled"
                                    color="black"
                                    loading={isSubmitting}
                                    onClick={submitForm}
                                    disabled={!isValid}
                                >
                                    Create
                                </Button>

                                <Button className={styles.manualButton} onClick={handleFormSwitchClick}>
                                    Or create Artist manually
                                </Button>
                            </div>
                        )}
                    />
                )}
            </ModalContent>
        </Modal>
    );
};

export default CreateArtistModal;
